import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '@/types';
import { Capsule, ContentItem, capsulesApi } from '@/services/capsulesApi';
import { usersApi } from '@/services/usersApi';
import { ProfileApiResponse } from '@/services/profileApi';
import { useAuth } from '@/contexts/AuthContext';
import M_UserProfileCard from '@/components/M_Molecules/M_UserProfileCard/M_UserProfileCard';
import M_CapsuleCard from '@/components/M_Molecules/M_CapsuleCard/M_CapsuleCard';
import { W_MasonryGrid } from '@/components/W_Wrappers/W_MasonryGrid/W_MasonryGrid';
import O_HeaderResponsive from '@/components/O_Organisms/O_Header/O_HeaderResponsive';
import { PATHS } from '@/constants/paths';
import styles from './UserProfilePage.module.css';
import M_SelectPicker from '@/components/M_Molecules/M_SelectPicker/M_SelectPicker';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/constants/translations';
import W_Loader from '@/components/W_Wrappers/W_Loader/W_Loader';
import W_NotFound from '@/components/W_Wrappers/W_NotFound/W_NotFound';
import W_CapsulesEmptyState from '@/components/W_Wrappers/W_CapsulesEmptyState/W_CapsulesEmptyState';

interface UserProfilePageProps {
  currentUser: User;
  onLogout: () => void;
}

export const UserProfilePage: React.FC<UserProfilePageProps> = ({
  currentUser: currentUserProp,
  onLogout,
}) => {
  const { handle: handleParam } = useParams<{ handle: string }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user: authUser } = useAuth();
  const currentUser = authUser || currentUserProp;
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showLoader, setShowLoader] = useState(true);
  const [posts, setPosts] = useState<
    Array<{
      id: string;
      title: string;
      image: string;
      avatars: string[];
      coauthors: number;
      commentsCount: number;
      isMyCapsule: boolean;
      ownerName: string;
      ownerId?: string;
    }>
  >([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filterType, setFilterType] = useState<'author' | 'coauthor'>('author');
  const centerColumnRef = useRef<HTMLDivElement>(null);

  const pickImageFromContent = useCallback(
    (content: ContentItem[] | undefined): string => {
      if (!content || content.length === 0) return '';
      const imageItem = content.find(c => c.type === 'image' && Boolean(c.url));
      return imageItem?.previewUrl || imageItem?.url || '';
    },
    []
  );

  const convertCapsuleToCardData = useCallback(
    (capsule: Capsule, userId: string) => {
      const coauthorAvatars = (capsule.privacy.coauthors || [])
        .map(c => c.avatar || '')
        .filter(Boolean) as string[];
      const avatars = [capsule.author.avatar || '', ...coauthorAvatars].filter(
        Boolean
      ) as string[];
      const coauthorsCount =
        capsule.privacy.coauthors?.length ||
        capsule.privacy.allowedUserIds?.length ||
        0;

      return {
        id: capsule.id,
        title: capsule.title,
        image:
          pickImageFromContent(capsule.content) ||
          capsule.imagePreview ||
          capsule.image ||
          '',
        avatars,
        coauthors: coauthorsCount,
        commentsCount: capsule.engagement.comments || 0,
        isMyCapsule: capsule.author.id === userId,
        ownerName: capsule.author.name || '',
        ownerId: capsule.author.id,
      };
    },
    [pickImageFromContent]
  );

  const filterCapsulesByType = (
    capsules: Capsule[],
    userId: string,
    type: 'author' | 'coauthor'
  ): Capsule[] => {
    if (type === 'author') {
      return capsules.filter(c => c.author.id === userId);
    } else {
      return capsules.filter(
        c =>
          c.privacy.coauthors?.some(ca => ca.id === userId) &&
          c.author.id !== userId
      );
    }
  };

  const mapApiUserToUser = useCallback((apiUser: ProfileApiResponse): User => {
    return {
      id: String(apiUser.id),
      handle: apiUser.handle,
      avatar: apiUser.avatar || undefined,
      personal: {
        firstName: apiUser.personal?.firstName || '',
        lastName: apiUser.personal?.lastName || '',
        middleName: apiUser.personal?.middleName || '',
        bio: apiUser.personal?.bio || '',
        dateOfBirth: apiUser.personal?.dateOfBirth || undefined,
        gender: (apiUser.personal?.gender as any) || 'not_selected',
      },
      contact: {
        email: apiUser.security?.email || '',
        phone: undefined,
        socials: apiUser.socials || [],
      },
      location: {
        label: '',
        birthPlace: apiUser.location?.birthPlace || {},
        residencePlace: apiUser.location?.residencePlace || {},
      },
      metrics: apiUser.metrics || {
        followers: 0,
        following: 0,
        coauthors: 0,
      },
      preferences: {
        language: 'ru',
        avatarVisibility:
          (apiUser.preferences?.avatarVisibility as any) || 'everyone',
        emailNotificationsEnabled: Boolean(
          (apiUser as any).preferences?.emailNotificationsEnabled
        ),
      },
      security: {
        password: apiUser.security?.password || '',
        hasSubscription: apiUser.security?.hasSubscription || false,
        lastLogin: undefined,
      },
      wallet: apiUser.wallet,
      createdAt: apiUser.createdAt,
      updatedAt: apiUser.updatedAt,
    };
  }, []);

  const loadMorePosts = useCallback(async () => {
    if (isLoading || !hasMore || !user?.id) return;

    setIsLoading(true);

    try {
      const response = await capsulesApi.getCapsules({
        authorId: user.id,
        page,
        limit: 8,
      });

      if (response.success && response.data) {
        const allCapsules = response.data.capsules || [];
        const filtered = filterCapsulesByType(allCapsules, user.id, filterType);
        const newPosts = filtered.map(c =>
          convertCapsuleToCardData(c, user.id)
        );

        if (newPosts.length === 0) {
          setHasMore(false);
        } else {
          setPosts(prev => [...prev, ...newPosts]);
          setPage(prev => prev + 1);
          setHasMore(response.data.hasMore);
        }
      } else {
        setHasMore(false);
      }
    } catch {
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [
    isLoading,
    hasMore,
    page,
    user?.id,
    filterType,
    convertCapsuleToCardData,
  ]);

  useEffect(() => {
    const el = centerColumnRef.current;
    if (!el) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollTop + clientHeight >= scrollHeight - 1000) {
        loadMorePosts();
      }
    };

    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [page, isLoading, hasMore, loadMorePosts]);

  useEffect(() => {
    const loadUser = async () => {
      if (
        handleParam &&
        currentUser?.handle &&
        handleParam === currentUser.handle
      ) {
        navigate(PATHS.PROFILE, { replace: true });
        return;
      }

      if (!handleParam) {
        setIsLoading(false);
        setShowLoader(false);
        return;
      }

      setIsLoading(true);
      setShowLoader(true);
      try {
        const searchResponse = await usersApi.searchUsers({
          q: handleParam,
          limit: 20,
        });

        if (!searchResponse.success || !searchResponse.data?.users?.length) {
          setUser(null);
          setIsLoading(false);
          setShowLoader(false);
          return;
        }

        const normalizedHandle = handleParam.startsWith('@')
          ? handleParam.slice(1)
          : handleParam;

        const profiles = await Promise.all(
          searchResponse.data.users.map(async u => {
            try {
              const userResponse = await usersApi.getUserInfo(u.id);
              if (userResponse.success && userResponse.data) {
                return userResponse.data;
              }
            } catch {
              // ignore
            }
            return null;
          })
        );

        const foundProfile = profiles.find(p => p?.handle === normalizedHandle);

        if (!foundProfile) {
          setUser(null);
          setIsLoading(false);
          setShowLoader(false);
          return;
        }

        const mappedUser = mapApiUserToUser(foundProfile);
        setUser(mappedUser);

        const capsulesResponse = await capsulesApi.getCapsules({
          authorId: String(foundProfile.id),
          page: 1,
          limit: 8,
        });

        if (capsulesResponse.success && capsulesResponse.data) {
          const allCapsules = capsulesResponse.data.capsules || [];
          const filtered = filterCapsulesByType(
            allCapsules,
            String(foundProfile.id),
            filterType
          );
          const userPosts = filtered.map(c =>
            convertCapsuleToCardData(c, String(foundProfile.id))
          );
          setPosts(userPosts);
          setPage(2);
          setHasMore(capsulesResponse.data.hasMore);
        }
      } catch {
        setUser(null);
      } finally {
        setIsLoading(false);
        setTimeout(() => {
          setShowLoader(false);
        }, 300);
      }
    };

    void loadUser();
  }, [
    handleParam,
    filterType,
    convertCapsuleToCardData,
    mapApiUserToUser,
    currentUser,
    navigate,
  ]);

  const [isFollowing, setIsFollowing] = useState(false);

  const handleFollowToggle = useCallback(async () => {
    if (!user?.id) return;

    const wasFollowing = isFollowing;
    setIsFollowing(!wasFollowing);

    try {
      let response;
      if (wasFollowing) {
        response = await usersApi.unfollowUser(user.id);
        if (response.success) {
          setUser(prevUser => {
            if (!prevUser) return prevUser;
            return {
              ...prevUser,
              metrics: {
                ...prevUser.metrics,
                followers: Math.max(0, prevUser.metrics.followers - 1),
              },
            };
          });
        } else {
          setIsFollowing(wasFollowing);
        }
      } else {
        response = await usersApi.followUser(user.id);
        if (response.success) {
          setUser(prevUser => {
            if (!prevUser) return prevUser;
            return {
              ...prevUser,
              metrics: {
                ...prevUser.metrics,
                followers: prevUser.metrics.followers + 1,
              },
            };
          });
        } else {
          setIsFollowing(wasFollowing);
        }
      }
    } catch {
      setIsFollowing(wasFollowing);
    }
  }, [user?.id, isFollowing]);

  const handleCopyId = () => {
    if (user?.handle) {
      navigator.clipboard.writeText(user.handle).catch(() => {});
    }
  };

  if (showLoader) {
    return <W_Loader isLoading={isLoading} />;
  }
  if (!user) {
    return <W_NotFound />;
  }

  return (
    <div className={styles.user_profile_page}>
      <O_HeaderResponsive />

      <main className={styles.content}>
        <div className={styles.left_column}>
          <M_UserProfileCard
            className={styles.profile_card_fill}
            user={user}
            isFollowing={isFollowing}
            onFollow={handleFollowToggle}
            onCopyId={handleCopyId}
          />
        </div>

        <div ref={centerColumnRef} className={styles.center_column}>
          <div className={styles.capsules_wrapper}>
            <M_SelectPicker
              options={[
                getTranslation(language, 'userProfile.filter.author'),
                getTranslation(language, 'userProfile.filter.coauthor'),
              ]}
              selectedOption={
                filterType === 'author'
                  ? getTranslation(language, 'userProfile.filter.author')
                  : getTranslation(language, 'userProfile.filter.coauthor')
              }
              onSelect={(option: string) => {
                const authorLabel = getTranslation(
                  language,
                  'userProfile.filter.author'
                );
                setFilterType(option === authorLabel ? 'author' : 'coauthor');
              }}
              background='white'
            />
            {posts.length > 0 ? (
              <W_MasonryGrid threeColumns={true}>
                {posts.map(post => (
                  <M_CapsuleCard
                    key={post.id}
                    id={post.id}
                    image={post.image}
                    title={post.title}
                    avatars={post.avatars}
                    coauthors={post.coauthors}
                    commentsCount={post.commentsCount}
                    isMyCapsule={post.isMyCapsule}
                    ownerName={post.ownerName}
                    ownerId={post.ownerId}
                  />
                ))}
              </W_MasonryGrid>
            ) : isLoading ? (
              <div className={styles.empty_area}>
                <span className='text-p-7'>
                  {getTranslation(language, 'common.loading')}
                </span>
              </div>
            ) : (
              <div className={styles.empty_area}>
                <W_CapsulesEmptyState translationKey='userProfile.noCapsules' />
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};
